'use client';

import { useEffect } from 'react';
import { clientAppLog } from '@/lib/logging/clientAppLog';

interface ContestCookieConsentProps {
  brandName?: string;
  contestLanguage?: string;
  country?: string;
}

/**
 * Valid themes from Cookie Consent Manager documentation
 */
const VALID_THEMES = [
  'austrian',
  'booking',
  'bookingcom',
  'bravofly',
  'crocieralastminute',
  'crocierissime',
  'croisieresoffres',
  'crucerisimo',
  'doveclub',
  'fluege',
  'holidayguru',
  'holidayPirates',
  'hotelscan',
  'lastminute',
  'lmn',
  'lufthansa',
  'melia',
  'mymiglia',
  'mystique',
  'naaukaa',
  'palladium',
  'playasol',
  'promovacances',
  'rumbo',
  'swiss',
  'thb',
  'thbhotels',
  'viajar',
  'vibra',
  'vibrahotels',
  'volagratis',
  'weg',
] as const;

/**
 * Normalize brand name to lowercase
 */
function normalizeBrand(brandName?: string | null): string {
  if (!brandName) {
    return 'lastminute';
  }
  return brandName.toLowerCase().trim();
}

/**
 * Get theme based on brand name
 * Returns brand name if it's a valid theme, otherwise defaults to brand name
 * or 'lastminute' as fallback
 */
function getTheme(brandName?: string | null): string {
  const brand = normalizeBrand(brandName);
  // Check if brand is a valid theme
  if (VALID_THEMES.includes(brand as any)) {
    return brand;
  }
  // Default to brand name or lastminute
  return brand || 'lastminute';
}

/**
 * Normalize language code to 2-character format
 * Valid languages: bg, cs, da, de, el, en, es, et, fi, fr, hr, hu, is, it, lv, nl, no, pl, pt, ro, sl, sv, sk
 */
function normalizeLanguage(language?: string | null): string {
  if (!language) {
    return 'en';
  }
  const match = language.match(/^([a-z]{2})/i);
  return match ? match[1].toLowerCase() : 'en';
}

/**
 * Normalize country code to 2-character uppercase format
 * Valid countries: AR, AT, AU, BG, BE, CL, CH, CY, CZ, DE, DK, EE, ES, FI, FR, GB, GR, HR, HU, IE, IN, IS, IT, LT, LU, LV, MT, MX, NL, NO, PL, PT, RO, SE, SI, SK, ZZ
 */
function normalizeCountry(country?: string | null): string {
  if (!country) {
    return 'GB';
  }
  const match = country.match(/^([a-z]{2})/i);
  return match ? match[1].toUpperCase() : 'GB';
}

/**
 * Get cookie consent manager version
 * Current version is 3 according to documentation
 */
function getCookieConsentVersion(): string {
  return '3';
}

/**
 * Get cookie consent manager host
 */
function getCookieConsentHost(): string {
  return 'https://assets.staticroot.com/cookie-consent-manager-ui/';
}

/**
 * Check if cookieGO event has already been fired
 * Checks platformData array for existing cookieGO event
 */
function eventAlreadyLaunched(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  // Check if platformData exists and has cookieGO event
  const platformData = (window as any).platformData;
  if (Array.isArray(platformData)) {
    return platformData.some((item: any) => item?.event === 'cookieGO');
  }

  return false;
}

/**
 * Execute vendors that require GDPR consent
 * This function should be called when cookieGO event is fired
 * or if the event has already been fired
 */
function executeVendors(): void {
  // TODO: Add vendor execution logic here
  // This is where you would initialize:
  // - CIA (Customer Intelligence Analytics)
  // - Casper
  // - Other tracking/analytics tools that require consent
  
  clientAppLog({
    level: 'INFO',
    codeSourceName: 'CookieConsent',
    logMessage: 'Executing vendors after consent',
    url: window.location.href,
  });
  
  // Example: You might want to dispatch a custom event for other components
  if (typeof window !== 'undefined') {
    const event = new CustomEvent('vendorsReady');
    window.dispatchEvent(event);
  }
}

/**
 * Contest Cookie Consent Component
 * Initializes LM cookie consent manager based on contest data (brand, language, country)
 * 
 * According to IT-Cookie Consent Manager documentation:
 * - Sets up cookie consent manager script with proper attributes
 * - Listens for cookieGO event to execute vendors
 * - Checks platformData if event already fired
 */
export function ContestCookieConsent({
  brandName,
  contestLanguage,
  country,
}: ContestCookieConsentProps) {
  useEffect(() => {
    // Normalize contest data
    const brand = normalizeBrand(brandName);
    const lang = normalizeLanguage(contestLanguage);
    const countryCode = normalizeCountry(country);
    const version = getCookieConsentVersion();
    const host = getCookieConsentHost();
    const theme = getTheme(brandName);
    const scriptSrc = `${host}${version}/cookie-consent-manager.js`;

    // Check if script is already loaded
    const existingScript = document.getElementById('cookie-consent-manager');
    if (existingScript) {
      clientAppLog({
        level: 'DEBUG',
        codeSourceName: 'CookieConsent',
        logMessage: 'Script already loaded',
        url: window.location.href,
      });
      
      // Still need to check if vendors should be executed
      if (eventAlreadyLaunched()) {
        executeVendors();
      } else {
        window.addEventListener('cookieGO', executeVendors, { once: true });
      }
      return;
    }

    // Create script element according to documentation format
    const script = document.createElement('script');
    script.id = 'cookie-consent-manager';
    script.src = scriptSrc;
    
    // Set data attributes for cookie consent manager configuration
    // Format matches documentation: data-host + data-version + src
    script.setAttribute('data-brand', brand);
    script.setAttribute('data-country', countryCode);
    script.setAttribute('data-lang', lang);
    script.setAttribute('data-theme', theme);
    script.setAttribute('data-host', host);
    script.setAttribute('data-version', version);

    // Handle script load
    script.onload = () => {
      clientAppLog({
        level: 'INFO',
        codeSourceName: 'CookieConsent',
        logMessage: `Cookie consent manager loaded successfully: brand=${brand}, lang=${lang}, country=${countryCode}, theme=${theme}, version=${version}`,
        url: window.location.href,
      });

      // Set up cookieGO event listener
      // Check if event already fired (in case script loaded after event)
      if (eventAlreadyLaunched()) {
        executeVendors();
      } else {
        // Listen for cookieGO event
        window.addEventListener('cookieGO', executeVendors, { once: true });
      }
    };

    // Handle script error
    script.onerror = () => {
      clientAppLog({
        level: 'ERROR',
        codeSourceName: 'CookieConsent',
        logMessage: `Failed to load cookie consent manager: scriptSrc=${scriptSrc}, brand=${brand}, lang=${lang}, country=${countryCode}`,
        url: window.location.href,
      });
    };

    // Append script to document body
    document.body.appendChild(script);

    // Cleanup function
    return () => {
      // Note: We don't remove the script on unmount as cookie consent
      // should persist across component remounts
      // Remove event listener if component unmounts before event fires
      window.removeEventListener('cookieGO', executeVendors);
    };
  }, [brandName, contestLanguage, country]);

  // Component doesn't render anything visible
  // Cookie consent banner is rendered by the loaded script
  return null;
}

